import { useMemo, useState, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ANALYTICS_EVENTS } from '@dunea/shared';
import { classNames } from '~/utils/classNames';
import { capture } from '~/lib/posthog';
import { pollBillingState } from '~/lib/billingRefresh';
import { openAccountSettings } from '~/lib/stores/accountSettings';
import {
  apiGetBillingOverview,
  apiGetBillingConfig,
  apiGetCreditBalance,
  apiGetCreditTopUpConfig,
  apiGetCreditPurchases,
  type BillingOverview,
} from '~/lib/api/billing';
import { accountTopUpKey, apiGetPlanTopUpOptions, apiStartPlanTopUp } from '~/lib/api/plans';
import { formatCredits, planScreenOf } from '~/lib/plans-model';
import { TopUpConfirmModal } from '~/components/account/TopUpConfirmModal';
import { TopUpCheckoutConfirmModal } from '~/components/account/TopUpCheckoutConfirmModal';
import { useNotify } from '~/components/account/ModalNotifications';

/** The top-up terms the screen sells at — always the server's; there is no built-in rate. */
interface TopUpTerms {
  creditsPerDollar: number;
  min: number;
  max: number;
  quickAmounts: number[];
}

function fmtMoney(amount: number | null, currency = 'usd'): string {
  if (amount == null) {
    return '—';
  }

  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency.toUpperCase() }).format(amount);
  } catch {
    return `$${amount.toFixed(2)}`;
  }
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const CARD = 'rounded-xl border border-hairline bg-raised';

// Status tinting as literal classes (text color + matching dot fill) for static extraction.
const PURCHASE_STATUS: Record<string, { label: string; text: string; dot: string }> = {
  paid: { label: 'Paid', text: 'text-success', dot: 'bg-success' },
  succeeded: { label: 'Paid', text: 'text-success', dot: 'bg-success' },
  pending: { label: 'Pending', text: 'text-warning', dot: 'bg-warning' },
  failed: { label: 'Failed', text: 'text-danger', dot: 'bg-danger' },
  refunded: { label: 'Refunded', text: 'text-muted', dot: 'bg-muted' },
};

// Fixed amount columns squeezed the date to an ellipsis on phones; they size to content there.
const GRID_COLS =
  'grid-cols-[minmax(0,1fr)_auto_auto_auto] gap-3 px-4 sm:grid-cols-[minmax(0,1fr)_92px_84px_84px] sm:gap-4 sm:px-4.5';

export function TopUpTab() {
  const { data: balance } = useQuery({
    queryKey: ['credit-balance'],
    queryFn: apiGetCreditBalance,
    staleTime: 30_000,
  });
  const {
    data: overview,
    isLoading: overviewLoading,
    isError: overviewError,
  } = useQuery<BillingOverview>({
    queryKey: ['billing-overview'],
    queryFn: apiGetBillingOverview,
    staleTime: 30_000,
  });
  const {
    data: purchases = [],
    isLoading: purchasesLoading,
    isError: purchasesError,
  } = useQuery({
    queryKey: ['credit-purchases'],
    queryFn: apiGetCreditPurchases,
    staleTime: 30_000,
  });

  /*
   * Top-ups follow the plan behind the balance, as the server sells them:
   *  - Plus/Pro: the plan-priced top-up (a credit costs what it does in the plan),
   *  - a grandfathered website plan: the website top-up,
   *  - Free: none (both servers refuse), so no rate is shown at all.
   */
  const { screen, app } = overview ? planScreenOf(overview) : { screen: 'free' as const, app: 'code' as const };

  const webTerms = useQuery({
    queryKey: ['credit-topup-config'],
    queryFn: apiGetCreditTopUpConfig,
    staleTime: 5 * 60_000,
    enabled: screen === 'legacy',
  });
  const planTerms = useQuery({
    queryKey: accountTopUpKey(app),
    queryFn: () => apiGetPlanTopUpOptions(app),
    staleTime: 5 * 60_000,
    enabled: screen === 'plan',
  });

  const terms: TopUpTerms | null =
    screen === 'legacy' && webTerms.data
      ? {
          creditsPerDollar: webTerms.data.creditsPerDollar,
          min: webTerms.data.min,
          max: webTerms.data.max,
          quickAmounts: webTerms.data.packs.map((p) => p.amountUsd),
        }
      : screen === 'plan' && planTerms.data
        ? {
            creditsPerDollar: planTerms.data.creditsPerDollar,
            min: planTerms.data.minUsd,
            max: planTerms.data.maxUsd,
            quickAmounts: planTerms.data.quickPicks.map((p) => p.usd),
          }
        : null;
  const termsQuery = screen === 'plan' ? planTerms : webTerms;
  const termsLoading = termsQuery.isLoading;
  const termsError = termsQuery.isError;
  const rate = terms?.creditsPerDollar ?? 0;
  const min = terms?.min ?? 0;
  const max = terms?.max ?? 0;
  const quickAmounts = terms?.quickAmounts ?? [];

  const queryClient = useQueryClient();
  const notify = useNotify();
  const [amountStr, setAmountStr] = useState('20');
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Set by handlePaid, which TopUpConfirmModal calls BEFORE onClose, so the close
  // handler can tell a completed purchase from an abandoned one. The hosted
  // checkout modal navigates away instead of closing, so it never false-positives.
  const paid = useRef(false);

  const amount = Number(amountStr);
  const amountValid = terms !== null && Number.isFinite(amount) && amount >= min && amount <= max;
  const estCredits = useMemo(() => (amountValid ? Math.round(amount * rate) : 0), [amountValid, amount, rate]);

  const { data: billingConfig } = useQuery({
    queryKey: ['billing-config'],
    queryFn: apiGetBillingConfig,
    staleTime: Infinity,
  });
  const isStripe = (billingConfig?.provider ?? 'stripe') === 'stripe';

  const pm = overview?.paymentMethod;
  const paymentLabel = pm ? `${pm.brand} ···· ${pm.last4}` : 'No card on file';

  // Top-ups come with a plan: the server refuses them on Free. Plan unknown
  // (still loading) is not treated as free.
  const isFree = !!overview && screen === 'free';
  const planUnknown = (overviewLoading && !overviewError) || (!isFree && termsLoading);

  function buy() {
    if (!amountValid || isFree) {
      return;
    }

    capture(ANALYTICS_EVENTS.TOPUP_SELECTED, { priceUsd: amount, credits: estCredits });

    // Confirm first for both providers. The modal then either pays in-app (Stripe)
    // or hands off to the provider's hosted checkout (Dodo).
    paid.current = false;
    setConfirmOpen(true);
  }

  function handlePaid() {
    paid.current = true;
    notify.success('Payment received — your credits will appear shortly.');
    // Fan the new balance out to the sidebar (stores) and the billing tabs (react-query).
    pollBillingState(queryClient);
  }

  function closeConfirm() {
    if (!paid.current) {
      capture(ANALYTICS_EVENTS.TOPUP_ABANDONED, { priceUsd: amount, credits: estCredits });
    }

    setConfirmOpen(false);
  }

  const currentBalance = balance?.currentBalance ?? overview?.creditsRemaining ?? null;

  return (
    <div>
      <h3 className="ui-serif m-0 mb-1.5 text-[24px]">Top up credits</h3>
      <p className="m-0 mb-4.5 max-w-[600px] text-[12.5px] leading-[1.55] text-muted">
        {isFree
          ? 'Credit top-ups come with Plus and Pro.'
          : 'Buy extra credits at any time. They add to the one balance you use on the website builder and in Dunea Code, and stay when your plan renews.'}
      </p>

      {/* Balance strip */}
      <div className="mb-3 flex flex-wrap items-center gap-5 rounded-xl border border-accent/20 bg-raised px-4.5 py-3.5">
        <div className="flex-none">
          <div className="ui-caption mb-1 text-accent">Current balance</div>
          <div className="flex items-baseline gap-1.5">
            <span className="ui-serif text-[30px] leading-none text-primary">
              {currentBalance != null ? formatCredits(currentBalance) : '—'}
            </span>
            <span className="text-[12.5px] text-muted">credits</span>
          </div>
        </div>

        {balance && (
          <div className="min-w-[200px] flex-1 border-accent/10 text-[12px] leading-normal text-muted sm:border-l sm:pl-5">
            <b className="font-medium text-primary">{formatCredits(balance.subscriptionCredits)}</b> from plan
            <span className="mx-1.5 text-faint">·</span>
            <b className="font-medium text-primary">{formatCredits(balance.purchasedCredits)}</b> purchased
          </div>
        )}
      </div>

      {/* Free plan: no purchase surface — the server sells top-ups on a plan only. */}
      {planUnknown ? (
        <div className={classNames(CARD, 'px-5 py-8 text-center text-[12.5px] text-muted')}>
          <span className="i-ph:spinner-gap mr-1.5 animate-spin" />
          Loading…
        </div>
      ) : isFree ? (
        <div className={classNames(CARD, 'px-6 py-7 text-center')}>
          <span className="i-ph:lock-simple text-[22px] text-warning" />
          <h4 className="ui-serif m-0 mb-1.5 mt-2 text-[19px] tracking-[-0.01em] text-primary">Top-ups need a plan</h4>
          <p className="mx-auto m-0 mb-4.5 max-w-[420px] text-[12.5px] leading-[1.55] text-muted">
            Choose Plus or Pro to buy extra credits. Your monthly credits start right away, in the same balance you use
            on the website builder and in Dunea Code.
          </p>
          <button type="button" onClick={() => openAccountSettings('plans', 'topup_tab')} className="ui-btn-accent">
            View plans
          </button>
        </div>
      ) : termsError || !terms ? (
        <div className={classNames(CARD, 'border-danger/30 px-5 py-6 text-center')}>
          <div className="mb-1 text-[12.5px] text-primary">Couldn't load top-up prices</div>
          <div className="mb-3 text-[11.5px] text-muted">Nothing can be bought until they load.</div>
          <button type="button" onClick={() => termsQuery.refetch()} className="ui-btn-secondary">
            Retry
          </button>
        </div>
      ) : (
        /* Two-column checkout: Amount picker (left) + Summary + Buy (right) */
        <div className="grid grid-cols-1 gap-3 md:grid-cols-[1.3fr_1fr]">
          {/* Amount picker */}
          <div className={classNames(CARD, 'px-5 py-4.5')}>
            <div className="ui-caption mb-2.5">Quick add</div>
            <div className="mb-4.5 flex flex-wrap gap-1.5">
              {quickAmounts.map((amt) => {
                const active = amountStr === String(amt);
                return (
                  <button
                    key={amt}
                    type="button"
                    onClick={() => setAmountStr(String(amt))}
                    className={classNames(
                      'cursor-pointer rounded-lg border px-3.5 py-1.5 text-[12.5px] font-medium tracking-[-0.005em] transition-colors duration-100 touch:min-h-10',
                      active
                        ? 'border-accent/35 bg-accent/10 text-accent'
                        : 'border-hairline bg-transparent text-secondary hover:bg-white/[0.03] hover:text-primary',
                    )}
                  >
                    ${amt}
                  </button>
                );
              })}
            </div>

            <div className="ui-caption mb-2">Custom amount</div>
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative max-w-[240px] flex-[1_1_180px]">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[13px] text-muted">
                  $
                </span>
                <input
                  type="number"
                  inputMode="decimal"
                  aria-label="Top-up amount in US dollars"
                  min={min}
                  max={max}
                  step="1"
                  value={amountStr}
                  onChange={(e) => setAmountStr(e.target.value)}
                  className={classNames(
                    'w-full rounded-lg border bg-sunken py-2.5 pl-6 pr-3 text-[13px] tabular-nums tracking-[-0.005em] text-primary outline-none',
                    amountStr && !amountValid ? 'border-danger' : 'border-hairline',
                  )}
                />
              </div>
              <div className="text-[12.5px] text-muted">
                = <span className="font-medium tabular-nums text-accent">{formatCredits(estCredits)}</span> credits
              </div>
            </div>
            {amountStr && !amountValid && (
              <div className="mt-2.5 flex items-center gap-1.5 text-[12px] text-danger">
                <span className="i-ph:warning-circle text-[13px]" />
                Enter an amount between ${min} and ${max}.
              </div>
            )}
          </div>

          {/* Summary + Buy */}
          <div className={classNames(CARD, 'flex flex-col px-5 pb-4 pt-4.5')}>
            <div className="ui-caption mb-3.5">Order summary</div>

            <div className="flex-1">
              {/* Credits — the hero line */}
              <div className="flex items-baseline justify-between px-0 pb-2.5 pt-0.5">
                <span className="text-[12.5px] text-muted">You'll get</span>
                <span className="inline-flex items-baseline gap-1">
                  <span className="ui-serif text-[22px] leading-none tabular-nums text-accent">
                    {amountValid ? `+${formatCredits(estCredits)}` : '—'}
                  </span>
                  <span className="text-[11.5px] text-muted">credits</span>
                </span>
              </div>

              <div className="my-1.5 h-px bg-hairline" />

              <div className="flex items-baseline justify-between py-1.5 text-[12.5px]">
                <span className="text-muted">Amount</span>
                <span className="tabular-nums text-primary">{fmtMoney(amountValid ? amount : null)}</span>
              </div>
              <div className="flex items-baseline justify-between gap-2 py-1.5 text-[12.5px]">
                <span className="flex-shrink-0 text-muted">Charged to</span>
                <span className="overflow-hidden text-ellipsis whitespace-nowrap text-[12px] text-secondary">
                  {paymentLabel}
                </span>
              </div>
            </div>

            <div className="mt-2 flex items-baseline justify-between border-t border-hairline pb-0.5 pt-3">
              <span className="ui-caption tracking-[0.06em]">Total</span>
              <span className="ui-serif text-[24px] leading-none tabular-nums text-primary">
                {fmtMoney(amountValid ? amount : null)}
              </span>
            </div>

            <button type="button" disabled={!amountValid} onClick={buy} className="ui-btn-accent mt-4 w-full">
              {amountValid ? `Pay ${fmtMoney(amount)}` : 'Enter an amount'}
            </button>
          </div>
        </div>
      )}

      {/* Purchase history */}
      <div className="ui-caption mb-2.5 mt-6">Purchase history</div>

      {purchasesLoading ? (
        <div className={classNames(CARD, 'px-5 py-6 text-center text-[12.5px] text-muted')}>
          <span className="i-ph:spinner-gap mr-1.5 animate-spin" />
          Loading…
        </div>
      ) : purchasesError ? (
        <div className={classNames(CARD, 'border-danger/30 px-5 py-5 text-center')}>
          <div className="text-[12.5px] text-danger">
            <span className="i-ph:warning-circle mr-1.5 align-[-2px]" />
            Couldn't load your purchases. Please try again shortly.
          </div>
        </div>
      ) : purchases.length === 0 ? (
        <div className={classNames(CARD, 'px-5 py-7 text-center')}>
          <span className="i-ph:receipt text-[20px] text-faint" />
          <div className="mt-1.5 text-[12.5px] text-muted">No credit purchases yet</div>
          <div className="mt-0.5 text-[11.5px] text-faint">They'll appear here after your first top-up</div>
        </div>
      ) : (
        <div className={classNames(CARD, 'overflow-hidden')}>
          <div
            className={classNames(
              'grid border-b border-hairline bg-white/[0.015] py-2.5 text-[10.5px] font-medium uppercase tracking-[0.06em] text-muted',
              GRID_COLS,
            )}
          >
            <span>Date</span>
            <span className="text-right">Amount</span>
            <span className="text-right">Credits</span>
            <span className="text-right">Status</span>
          </div>

          {purchases.map((p) => {
            const statusMeta = PURCHASE_STATUS[p.status?.toLowerCase() ?? ''] ?? {
              label: p.status ?? '—',
              text: 'text-muted',
              dot: 'bg-muted',
            };
            return (
              <div
                key={p.id}
                className={classNames(
                  'grid items-center border-t border-hairline py-3 text-[12.5px] transition-colors duration-100 hover:bg-white/[0.02]',
                  GRID_COLS,
                )}
              >
                <span className="overflow-hidden text-ellipsis whitespace-nowrap tracking-[-0.005em] text-primary">
                  {fmtDate(p.date)}
                </span>
                <span className="text-right tabular-nums text-secondary">{fmtMoney(p.amountPaid, p.currency)}</span>
                <span className="text-right font-medium tabular-nums text-accent">
                  +{formatCredits(p.creditsGranted)}
                </span>
                <span
                  className={classNames('inline-flex items-center justify-end gap-1.5 text-[11px]', statusMeta.text)}
                >
                  <span className={classNames('h-[5px] w-[5px] flex-shrink-0 rounded-full', statusMeta.dot)} />
                  {statusMeta.label}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {confirmOpen &&
        amountValid &&
        !isFree &&
        (screen === 'plan' ? (
          // Plus/Pro top-ups are Dodo-hosted, priced at the plan's own credit price
          <TopUpCheckoutConfirmModal
            amount={amount}
            credits={estCredits}
            startCheckout={(usd) => apiStartPlanTopUp(usd, app)}
            onClose={closeConfirm}
          />
        ) : isStripe ? (
          <TopUpConfirmModal amount={amount} credits={estCredits} onPaid={handlePaid} onClose={closeConfirm} />
        ) : (
          <TopUpCheckoutConfirmModal amount={amount} credits={estCredits} onClose={closeConfirm} />
        ))}
    </div>
  );
}
